import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState, type SubmitEvent } from "react";
import { z } from "zod";
import { AuthShell } from "@/components/auth-shell";
import { ErrorText } from "@/components/page";
import { RecoveryKeyCard } from "@/components/recovery-key-card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { session, signup } from "@/lib/session";

export const Route = createFileRoute("/signup")({
  validateSearch: z.object({ invite: z.string().optional() }),
  beforeLoad: () => {
    if (session.get().status !== "anon") throw redirect({ to: "/" });
  },
  component: SignupPage,
});

function SignupPage() {
  const nav = useNavigate();
  const { invite } = Route.useSearch();
  const info = useQuery({
    queryKey: ["invite", invite],
    queryFn: () => api.auth.inviteInfo({ invite_token: invite ?? "" }),
    enabled: Boolean(invite),
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recoveryKey, setRecoveryKey] = useState<string | null>(null);

  if (!invite) {
    return (
      <AuthShell title="Invite required">
        <Alert>
          <AlertTitle>EnvStash is invite-only right now</AlertTitle>
          <AlertDescription>Ask an administrator for an invite link, then open it here.</AlertDescription>
        </Alert>
      </AuthShell>
    );
  }

  if (recoveryKey) {
    return (
      <AuthShell title="Account created" description="One last thing before you continue.">
        <RecoveryKeyCard recoveryKey={recoveryKey} onDone={() => void nav({ to: "/" })} />
      </AuthShell>
    );
  }

  const lockedEmail = info.data?.email ?? null;
  const onSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match");
      return;
    }
    if (password.length < 10) {
      setError("Use at least 10 characters");
      return;
    }
    setError(null);
    try {
      const rk = await signup({ invite, email: lockedEmail ?? email, name, password }, setBusy);
      setRecoveryKey(rk);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AuthShell title="Create your account" description="Everything is encrypted on this device before it is stored.">
      {info.data && !info.data.valid ? (
        <Alert className="mb-4">
          <AlertTitle>This invite is no longer valid</AlertTitle>
          <AlertDescription>It may have expired or already been used.</AlertDescription>
        </Alert>
      ) : null}
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="name">Name</Label>
          <Input id="name" required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            required
            autoComplete="username"
            value={lockedEmail ?? email}
            disabled={Boolean(lockedEmail)}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="confirm">Confirm password</Label>
          <Input
            id="confirm"
            type="password"
            required
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        <ErrorText error={error} />
        <Button type="submit" className="w-full" disabled={busy !== null || info.data?.valid === false}>
          {busy ?? "Create account"}
        </Button>
      </form>
    </AuthShell>
  );
}
